/*
 * Video-Slider mit Element-Pool (v4)
 * - Die Slides enthalten nur ein Poster-<img> und die Video-URL als data-Attribut.
 * - Es existieren genau 3 <video>-Elemente (Pool). Sie werden dem vorherigen,
 *   aktiven und naechsten Slide zugewiesen und wandern mit dem Nutzer mit.
 *   Damit ist Swipen in beide Richtungen nahtlos, iOS sieht aber nie mehr als
 *   3 Media-Elemente und Swiper verschiebt nie ein Slide mit laufendem Video.
 * - Fortschritts-Watchdog: haengt das aktive Video, erscheint ein Play-Button.
 *   Haengt es auf iOS wiederholt, faellt der Slider auf 2 Elemente zurueck
 *   (aktiv + naechster).
 */
if (!customElements.get("c-video-slider")) {
  class CVideoSlider extends HTMLElement {
    constructor() {
      super();
      this.isVisible = false;
      this.playTimer = null;
      this.stalls = 0;
      this.degraded = false;
      this.pool = [];
    }

    connectedCallback() {
      this.createPool();
      this.initSlider();
      this.addVolumeButtonLogic();
      this.addPlayButtonLogic();
      this.setupVisibilityObserver();
    }

    get isIOS() {
      return /iP(hone|ad|od)/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    }

    /* ---------- Pool ---------- */

    createPool() {
      for (let i = 0; i < 3; i++) {
        const v = document.createElement("video");
        v.setAttribute("playsinline", "");
        v.setAttribute("webkit-playsinline", "");
        v.setAttribute("muted", "");
        v.setAttribute("loop", "");
        v.setAttribute("preload", "auto");
        v.muted = true;
        v.className = "vs-video";
        const show = () => {
          v.classList.add("is-playing");
          const slide = v.closest(".swiper-slide");
          const btn = slide && slide.querySelector(".btn_play");
          if (btn) btn.hidden = true;
        };
        v.addEventListener("playing", show);
        v.addEventListener("timeupdate", () => { if (v.currentTime > 0.1) show(); });
        v.addEventListener("error", () => { v.dataset.playError = "media-error:" + (v.error && v.error.code); });
        this.pool.push(v);
      }
    }

    freeVideo() {
      return this.pool.find(v => !v.parentElement) || null;
    }

    attach(v, slide) {
      const host = slide.querySelector(".product-image");
      if (!host) return;
      v.classList.remove("is-playing");
      delete v.dataset.playError;
      v.src = slide.dataset.videoSrc;
      const anchor = host.querySelector(".btn_play");
      host.insertBefore(v, anchor || null);
    }

    release(v) {
      if (!v.paused) v.pause();
      v.classList.remove("is-playing");
      v.removeAttribute("src");
      try { v.load(); } catch (e) {}
      if (v.parentElement) v.parentElement.removeChild(v);
    }

    /* ---------- Swiper ---------- */

    initSlider() {
      this.slider = new Swiper(this, {
        slidesPerView: 1.35,
        spaceBetween: 16,
        speed: 150,
        centeredSlides: true,
        loop: true,
        slideToClickedSlide: true,
        breakpoints: {
          600: { slidesPerView: 1.75 },
          900: { slidesPerView: 2.5 },
          1200: { slidesPerView: 5, spaceBetween: 24 }
        },
        navigation: {
          nextEl: this.querySelector(".swiper-button-next-cm"),
          prevEl: this.querySelector(".swiper-button-prev-cm")
        },
        on: {
          init: () => { this.classList.add("swiper-ready"); }
        }
      });

      this.slider.on("slideChange", () => {
        this.resetAllVolumeIcons();
        this.sync();
      });
    }

    /* Slides im Fenster: vorher / aktiv / naechster (DOM-Nachbarn im Loop) */
    windowSlides() {
      const s = this.slider.slides;
      const n = s.length;
      const a = this.slider.activeIndex;
      const active = s[a];
      const prev = s[(a - 1 + n) % n];
      const next = s[(a + 1) % n];
      const list = (this.isIOS && this.degraded) ? [active, next] : [prev, active, next];
      return { active, targets: list.filter((x, i, arr) => x && arr.indexOf(x) === i) };
    }

    /* ---------- Kernlogik ---------- */

    sync() {
      const { active, targets } = this.windowSlides();
      if (!active) return;

      /* 1) Videos freigeben, deren Slide aus dem Fenster gefallen ist */
      this.pool.forEach(v => {
        const slide = v.closest(".swiper-slide");
        if (v.parentElement && (!slide || targets.indexOf(slide) === -1)) this.release(v);
      });

      /* Ausserhalb des Viewports wird nichts geladen (Speed: kein Video-Traffic
         beim Seitenaufruf, solange der Slider nicht sichtbar ist) */
      if (!this.isVisible) {
        this.pool.forEach(v => { if (!v.paused) v.pause(); });
        return;
      }

      /* 2) Slides im Fenster ohne Video bekommen eines (aktiv zuerst) */
      [active].concat(targets.filter(s => s !== active)).forEach(slide => {
        if (!slide.querySelector("video")) {
          const v = this.freeVideo();
          if (v) this.attach(v, slide);
        }
      });

      /* 3) Abspielen: alle im Fenster stumm, aktiv bekommt den Watchdog */
      const activeVideo = active.querySelector("video");
      const playBtn = active.querySelector(".btn_play");
      targets.forEach(slide => {
        const v = slide.querySelector("video");
        if (!v) return;
        v.muted = true;
        if (v.paused) this.startVideo(v, slide === active, playBtn);
      });

      clearTimeout(this.playTimer);
      if (activeVideo && playBtn) {
        if (!activeVideo.paused && activeVideo.readyState >= 3) playBtn.hidden = true;
        const t0 = activeVideo.currentTime;
        this.playTimer = setTimeout(() => {
          if (!this.isVisible) return;
          if (active !== this.windowSlides().active) return;
          const stuck = activeVideo.paused || activeVideo.currentTime - t0 < 0.2;
          if (!stuck) { this.stalls = 0; return; }
          this.stalls++;
          activeVideo.dataset.playError = activeVideo.dataset.playError ||
            ("stalled:rs" + activeVideo.readyState + ":ns" + activeVideo.networkState);
          playBtn.hidden = false;
          /* iOS: nach 2 Haengern auf 2 Elemente zurueckfallen */
          if (this.isIOS && this.stalls >= 2 && !this.degraded) {
            this.degraded = true;
            this.sync();
          }
        }, 3000);
      }
    }

    startVideo(v, isActive, playBtn) {
      v.muted = true;
      const p = v.play();
      if (!p || !p.then) return;
      p.then(() => {
        if (isActive && playBtn) playBtn.hidden = true;
      }).catch(err => {
        v.dataset.playError = (err && err.name) || "unknown";
        if (isActive && playBtn) playBtn.hidden = false;
      });
    }

    /* ---------- Buttons ---------- */

    addPlayButtonLogic() {
      this.querySelectorAll(".btn_play").forEach(btn => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const slide = btn.closest(".swiper-slide");
          let video = slide && slide.querySelector("video");
          if (!video) {
            video = this.freeVideo();
            if (video) this.attach(video, slide);
          }
          if (!video) return;
          video.muted = true;
          if (!video.paused && video.readyState < 3) video.load();
          const p = video.play();
          if (p && p.then) p.then(() => { btn.hidden = true; }).catch(err => {
            video.dataset.playError = "click:" + ((err && err.name) || "unknown");
          });
        });
      });
    }

    resetAllVolumeIcons() {
      this.querySelectorAll(".btn_volumn").forEach(btn => {
        btn.querySelector(".sounded").style.display = "none";
        btn.querySelector(".muted").style.display = "block";
      });
    }

    addVolumeButtonLogic() {
      this.querySelectorAll(".btn_volumn").forEach(btn => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const slide = btn.closest(".swiper-slide");
          const video = slide.querySelector("video");
          if (!video || !slide.classList.contains("swiper-slide-active")) return;
          video.muted = !video.muted;
          btn.querySelector(".sounded").style.display = video.muted ? "none" : "block";
          btn.querySelector(".muted").style.display = video.muted ? "block" : "none";
        });
      });
    }

    /* ---------- Sichtbarkeit ---------- */

    setupVisibilityObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.resetAllVolumeIcons();
            this.sync();
          } else {
            clearTimeout(this.playTimer);
            this.pool.forEach(v => { if (!v.paused) v.pause(); });
            this.resetAllVolumeIcons();
          }
        });
      }, { threshold: 0.25 });
      observer.observe(this);
    }
  }

  customElements.define("c-video-slider", CVideoSlider);
}
